import React, { useContext, useState, useEffect } from 'react';
import CasesAPI from 'api/CasesAPI';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import { Grid, Modal } from '@mui/material';
import { StyledTab, StyledTabs, NextButton, BackButton } from 'components/Common/MUIStyledComponents';
import TabPanel from 'components/Common/TabPanel';
import BackNavButton from 'components/Common/BackButton';
import EntityForm from 'components/CaseManagement/EntityForm';
import CommodityForm from 'components/CaseManagement/CommodityForm';
import PDFUpload from 'components/CaseManagement/PDFUpload';
import Comments from 'components/CaseManagement/Comments';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import CaseName from 'components/CaseManagement/CaseName';
import ResultsTable from 'components/CaseManagement/ResultsTable';
import SearchedProps from 'components/CaseManagement/SearchedProps';
import CaseShareModal from 'components/CaseManagement/CaseShareModal';
import CaseDeleteModal from 'components/CaseManagement/CaseDeleteModal';
import { CustomLabel, CustomSwitch } from 'components/Common/MUIStyledComponents';

export interface IEntityProps {
  entity: {
    firstName: string;
    lastName: string;
    companyName: string;
    DBA: string;
    address: string;
    city: string;
    country: string;
    continent: string;
    postalCode: string;
  }
}

export interface ICommodityProps {
  commodities: {
    commodityName: string;
    harmonizedScheduleCode: string;
    countryOfOrigin: string;
    transhipmentCountry: string;
    destinationCountry: string;
  }[]
}

export interface IDocumentsProps {
  documents: {
    uploadedFile: {
      name: string;
      base64: string;
    };
  }[]
  setFieldValue: any;
  resetForm: any;
}

const formValSchema = Yup.object().shape({
  basics: Yup.object().shape({
    date: Yup.string().required('Date is required'),
  })
});

const Case: React.FC = (props: any) => {
  const { cur_user, userOpts } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dt = new Date();
  const dtWithoutTz = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');
  const [ case_id, setCaseID ] = useState(props.match.params.case_id);
  const [ shareOpen, setShareOpen ] = useState<boolean>(false);
  const [ deleteOpen, setDeleteOpen ] = useState<boolean>(false);
  const [ notify, setNotify ] = useState<boolean>(false);

  // PAGE STATE
  const [ tabValue, setTabValue ] = useState(0);
  const [ existingCase, setExistingCase ] = useState(false);
  const [ cur_case, setCase ] = useState<{
    isLoaded: boolean;
    error: string | undefined;
    data: any;
  }>({ isLoaded: false, error: undefined, data: {} });

  // TOP LEVEL TABS
  const tabChange = (e: any, newValue: number) => {
    setTabValue(newValue);
  };

  const a11yProps = (index: number) => {
    return {
      'id': `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const handleEdit = async() => {
    if (cur_case.data.editing?.kc_id && cur_case.data.editing?.kc_id !== cur_user.basics?.id) {
      return;
    }
    let userInfo: any = {};
    if (cur_case.data.editing?.firstName) {
      userInfo = {
        kc_id: '',
        firstName: '',
        lastName: '',
        email: '',
        kcSynced: false,
        hidden: false,
      };
    } else {
      userInfo = {
        kc_id: cur_user.basics?.id,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
        email: cur_user.basics?.email,
        kcSynced: true,
        hidden: false,
      };
    }
    const update: any = {
      _id: case_id,
      editing: userInfo
    };
    const resp = await CasesAPI.updateCase(update);
    if(resp?.success && resp.result.editing?.firstName){
      enqueueSnackbar('You may now comment on or edit this case', { variant: 'info' });
      setCase({ isLoaded: true, error: undefined, data: resp.result });
    } else if(resp?.success && !resp.result.editing?.firstName) {
      enqueueSnackbar('You are no longer editing this case', { variant: 'info' });
      setCase({ isLoaded: true, error: undefined, data: resp.result });
    } else if(resp) {
      const error_msg = 'There was an error, please try again and notify the BITE Team if the problem continues';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const handleSubmit = async(values: any) => {
    if (!existingCase) {
      setCase({ isLoaded: false, error: undefined, data: {} });
      values.docType = 'report';
      // Check for Blank Commodity when expecting an array of Objects
      if (values.commodities !== undefined) {
        values.commodities.forEach((commodity: ICommodityProps | string, index: number) => {
          if (commodity === '') {
            values.commodities[index] = {};
          }
        }
        );
      }
      // Check for Blank Documents when expecting an array of Objects
      if (values.documents !== undefined) {
        values.documents.forEach((document: IDocumentsProps | string, index: number) => {
          if (document === '') {
            values.documents[index] = {};
          }
        }
        );
      }
      const new_case = {
        groupId: cur_user.selected_team.id,
        ...values.basics,
        ...values,
      };
      const resp = await CasesAPI.createCase({ newCase: new_case });
      if(resp?.success){
        enqueueSnackbar('Successfully created new case', { variant: 'info' });
        history.push(`/analytics-vault/case/${resp.result._id}`);
        setCaseID(resp.result._id);
      }else if(resp){
        const error_msg = 'Error saving new case. Uploaded file(s) may be too large.';
        enqueueSnackbar(error_msg, { variant: 'error' });
      }
    } else {
      const updated_case = {
        ...values?.basics,
        _id: cur_case.data._id,
        groupId: cur_user.selected_team.id,
        docType: values.docType,
        status: values.status,
        ticketName: values.ticketName,
        entity: values.entity,
        commodities: values.commodities,
        documents: values.documents,
        comments: values.comments,
        sharedWith: values.sharedWith,
        results: values.docType === 'bulkScore' ? JSON.stringify(values.results) : '',
        searchedProps: values.docType === 'bulkScore' ? JSON.stringify(values.searchedProps) : '',
      };

      const resp = await CasesAPI.updateCase(updated_case);
      if(resp?.success){
        // if(approval) {
        //   handleApproveTicketNotification(updated_case, cur_user, formatted_dt, cur_case.data.createdBy, case_id);
        // }
        // if (notify) {
        //   if (updated_case.sharedWith.length === 0) {
        //     return;
        //   }
        //   updated_case.sharedWith.forEach(async(user: string) => {
        //     handleUpdateTicketNotification(updated_case, cur_user, formatted_dt, user, case_id);
        //   });
        // }
        enqueueSnackbar('Successfully saved case', { variant: 'success' });
      }else{
        const err_text = 'Error updating case. Uploaded file(s) may be too large.';
        enqueueSnackbar(err_text, { variant: 'error' });
      }
    };
  };

  const handleDeleteCase = async(case_id: string) => {
    const resp = await CasesAPI.deleteCase(case_id);
    if(resp?.success){
      enqueueSnackbar('Removed case', { variant: 'info' });
      history.push('/analytics-vault');
    }else if(resp){
      const error_msg = `Error removing case: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  useEffect(() => {
    if (case_id) {
      CasesAPI.getCase(case_id)?.then((res) => {
        if(res?.success){
          setCase({ isLoaded: true, error: undefined, data: res.result });
          setExistingCase(true);
        }else{
          const err_text = `Error loading case: ${res?.result}`;
          enqueueSnackbar(err_text, { variant: 'error' });
        }
      });
    } else {
      setCase({ isLoaded: true, error: undefined, data: {} });
    }
  }, [ case_id ]);

  if(!cur_case.isLoaded){
    return (
      <div className='spinner-spacer-table'>
        <div className='loader'>Loading...</div>
      </div>
    );
  }

  window.addEventListener('beforeunload', (e:any) => {
    if (cur_case.data.editing?.kc_id === cur_user.basics?.id) {
      handleEdit();
    }
  });

  return (
    <Formik
      initialValues={{
        docType: cur_case?.data?.docType,
        status: cur_case?.data?.status ? cur_case.data.status : 'draft',
        basics: {
          date: formatted_dt,
        },
        ticketName: cur_case?.data?.ticketName,
        entity: cur_case?.data?.entity,
        commodities: cur_case?.data?.commodities,
        documents: cur_case?.data?.documents,
        comments: cur_case?.data?.comments,
        sharedWith: cur_case?.data?.sharedWith,
        results: cur_case?.data?.docType === 'bulkScore' ? JSON.parse(cur_case?.data?.results) : '',
        searchedProps: cur_case?.data?.docType === 'bulkScore' ? JSON.parse(cur_case?.data?.searchedProps) : '',
      }}
      validationSchema={formValSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, resetForm, submitForm }) =>
        <Form>
          <div className='vault-page-spacing'>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className='vault-page-header'>
                  <div className='display-flex align-items-center'>
                    <div className='back-btn-spacer'></div>
                    <BackNavButton />
                    <h2>{existingCase ? 'Results' : 'New Case'}</h2>
                  </div>
                  <p className='vault-page-header-words'>Date created: {values.basics.date ? values.basics.date : formatted_dt}</p>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className='vault-tabs-spacer'>
                  {cur_case.data.docType !== 'bulkScore' ? (
                    <StyledTabs value={tabValue} onChange={tabChange} centered>
                      <StyledTab label='Entity' {...a11yProps(0)}/>
                      <StyledTab label='Commodities' {...a11yProps(1)} />
                    </StyledTabs>
                  ) : (
                    <div></div>
                  )}
                  {existingCase ? (
                    <div>
                      { cur_case.data.editing?.kc_id && cur_case.data.editing.kc_id !== cur_user.basics?.id ? (
                        <div className='vault-actions-opts'>
                          <div className='action-group'>
                            <p>{cur_case.data.editing.firstName} {cur_case.data.editing.lastName} is currently editing this ticket</p>
                          </div>
                        </div>
                      ) : (
                        <div className='vault-actions-opts'>
                          <div className='action-group' onClick={handleEdit}>
                            <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 19 19' fill='none'>
                              <path d='M2.39686 15.0963C2.43515 14.7517 2.45429 14.5794 2.50642 14.4184C2.55268 14.2755 2.61802 14.1396 2.70069 14.0142C2.79388 13.8729 2.91645 13.7503 3.1616 13.5052L14.1669 2.49992C15.0873 1.57945 16.5797 1.57945 17.5002 2.49993C18.4207 3.4204 18.4207 4.91279 17.5002 5.83326L6.49493 16.8385C6.24978 17.0836 6.12721 17.2062 5.9859 17.2994C5.86054 17.3821 5.72457 17.4474 5.5817 17.4937C5.42067 17.5458 5.24838 17.5649 4.9038 17.6032L2.0835 17.9166L2.39686 15.0963Z' stroke='#C2C2C2' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                            </svg>
                            {cur_case.data.editing?.kc_id === cur_user.basics?.id ? (
                              <p>Exit Editing</p>
                            ) : !cur_case.data.editing?.kc_id ? (
                              <p>Edit / Comment</p>
                            ) : (
                              <p>{cur_case.data.editing.firstName} {cur_case.data.editing.lastName} is Editing</p>
                            )}
                          </div>
                          {cur_user.selected_team.attributes.team_admin.includes(cur_user.basics?.email) && values.status !== 'complete' ? (
                            <div className='action-group' onClick={() => {
                              setFieldValue('status', 'complete');
                              submitForm();
                            }}>
                              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                                <path d='M7.6575 11.0625C7.72722 11.1328 7.81017 11.1886 7.90157 11.2267C7.99296 11.2647 8.09099 11.2843 8.19 11.2843C8.28901 11.2843 8.38704 11.2647 8.47843 11.2267C8.56983 11.1886 8.65278 11.1328 8.7225 11.0625L11.7825 8.0025C11.9237 7.86127 12.0031 7.66973 12.0031 7.47C12.0031 7.27027 11.9237 7.07873 11.7825 6.9375C11.6413 6.79627 11.4497 6.71693 11.25 6.71693C11.0503 6.71693 10.8587 6.79627 10.7175 6.9375L8.19 9.4725L7.2825 8.5575C7.14127 8.41627 6.94973 8.33693 6.75 8.33693C6.55027 8.33693 6.35873 8.41627 6.2175 8.5575C6.07627 8.69873 5.99693 8.89027 5.99693 9.09C5.99693 9.28973 6.07627 9.48127 6.2175 9.6225L7.6575 11.0625ZM15.75 1.5H2.25C2.05109 1.5 1.86032 1.57902 1.71967 1.71967C1.57902 1.86032 1.5 2.05109 1.5 2.25V15.75C1.5 15.9489 1.57902 16.1397 1.71967 16.2803C1.86032 16.421 2.05109 16.5 2.25 16.5H15.75C15.9489 16.5 16.1397 16.421 16.2803 16.2803C16.421 16.1397 16.5 15.9489 16.5 15.75V2.25C16.5 2.05109 16.421 1.86032 16.2803 1.71967C16.1397 1.57902 15.9489 1.5 15.75 1.5ZM15 15H3V3H15V15Z' fill='#C2C2C2'/>
                              </svg>
                              <p>Approve</p>
                            </div>
                          ) : (
                            <></>
                          )}
                          <div className='action-group' onClick={() => setShareOpen(true)}>
                            <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                              <path d='M13.5 10.5008C13.0569 10.5037 12.6199 10.6048 12.2205 10.7967C11.8211 10.9887 11.4692 11.2667 11.19 11.6108L7.36499 9.84832C7.54483 9.29763 7.54483 8.70401 7.36499 8.15332L11.19 6.39082C11.6412 6.93529 12.2702 7.30288 12.9661 7.4287C13.662 7.55452 14.3799 7.43049 14.9932 7.0785C15.6065 6.7265 16.0758 6.16918 16.3182 5.50488C16.5605 4.84057 16.5605 4.112 16.318 3.44775C16.0755 2.78349 15.6061 2.22625 14.9927 1.87437C14.3794 1.52249 13.6614 1.39859 12.9656 1.52454C12.2697 1.65048 11.6408 2.01818 11.1896 2.56274C10.7385 3.1073 10.4943 3.7937 10.5 4.50082C10.5022 4.67949 10.5198 4.85764 10.5525 5.03332L6.59249 6.85582C6.1703 6.443 5.63567 6.16396 5.05559 6.05364C4.47552 5.94332 3.87578 6.00664 3.33153 6.23567C2.78728 6.46469 2.32271 6.84923 1.99604 7.34111C1.66937 7.833 1.49512 8.41034 1.49512 9.00082C1.49512 9.59129 1.66937 10.1686 1.99604 10.6605C2.32271 11.1524 2.78728 11.5369 3.33153 11.766C3.87578 11.995 4.47552 12.0583 5.05559 11.948C5.63567 11.8377 6.1703 11.5586 6.59249 11.1458L10.5525 12.9683C10.5198 13.144 10.5022 13.3221 10.5 13.5008C10.5 14.0942 10.6759 14.6742 11.0056 15.1675C11.3352 15.6609 11.8038 16.0454 12.3519 16.2725C12.9001 16.4995 13.5033 16.5589 14.0853 16.4432C14.6672 16.3274 15.2018 16.0417 15.6213 15.6221C16.0409 15.2026 16.3266 14.668 16.4423 14.0861C16.5581 13.5041 16.4987 12.9009 16.2716 12.3528C16.0446 11.8046 15.66 11.3361 15.1667 11.0064C14.6734 10.6768 14.0933 10.5008 13.5 10.5008ZM13.5 3.00082C13.7967 3.00082 14.0867 3.08879 14.3333 3.25361C14.58 3.41844 14.7723 3.6527 14.8858 3.92679C14.9993 4.20088 15.029 4.50248 14.9712 4.79345C14.9133 5.08443 14.7704 5.3517 14.5607 5.56148C14.3509 5.77126 14.0836 5.91412 13.7926 5.972C13.5017 6.02987 13.2001 6.00017 12.926 5.88664C12.6519 5.77311 12.4176 5.58085 12.2528 5.33417C12.088 5.0875 12 4.79749 12 4.50082C12 4.10299 12.158 3.72146 12.4393 3.44016C12.7206 3.15885 13.1022 3.00082 13.5 3.00082ZM4.49999 10.5008C4.20332 10.5008 3.91331 10.4128 3.66664 10.248C3.41996 10.0832 3.2277 9.84893 3.11417 9.57484C3.00064 9.30075 2.97093 8.99915 3.02881 8.70818C3.08669 8.41721 3.22955 8.14994 3.43933 7.94016C3.64911 7.73038 3.91638 7.58752 4.20736 7.52964C4.49833 7.47176 4.79993 7.50147 5.07402 7.615C5.3481 7.72853 5.58237 7.92079 5.74719 8.16746C5.91202 8.41414 5.99999 8.70415 5.99999 9.00082C5.99999 9.39864 5.84196 9.78017 5.56065 10.0615C5.27935 10.3428 4.89782 10.5008 4.49999 10.5008ZM13.5 15.0008C13.2033 15.0008 12.9133 14.9128 12.6666 14.748C12.42 14.5832 12.2277 14.3489 12.1142 14.0748C12.0006 13.8008 11.9709 13.4992 12.0288 13.2082C12.0867 12.9172 12.2296 12.6499 12.4393 12.4402C12.6491 12.2304 12.9164 12.0875 13.2074 12.0296C13.4983 11.9718 13.7999 12.0015 14.074 12.115C14.3481 12.2285 14.5824 12.4208 14.7472 12.6675C14.912 12.9141 15 13.2041 15 13.5008C15 13.8986 14.842 14.2802 14.5607 14.5615C14.2793 14.8428 13.8978 15.0008 13.5 15.0008Z' fill='#C2C2C2'/>
                            </svg>
                            <p>Share</p>
                          </div>
                          <div className='action-group' onClick={() => setDeleteOpen(true)}>
                            <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                              <path d='M7.5 13.5C7.69891 13.5 7.88968 13.421 8.03033 13.2803C8.17098 13.1397 8.25 12.9489 8.25 12.75V8.25C8.25 8.05109 8.17098 7.86032 8.03033 7.71967C7.88968 7.57902 7.69891 7.5 7.5 7.5C7.30109 7.5 7.11032 7.57902 6.96967 7.71967C6.82902 7.86032 6.75 8.05109 6.75 8.25V12.75C6.75 12.9489 6.82902 13.1397 6.96967 13.2803C7.11032 13.421 7.30109 13.5 7.5 13.5ZM15 4.5H12V3.75C12 3.15326 11.7629 2.58097 11.341 2.15901C10.919 1.73705 10.3467 1.5 9.75 1.5H8.25C7.65326 1.5 7.08097 1.73705 6.65901 2.15901C6.23705 2.58097 6 3.15326 6 3.75V4.5H3C2.80109 4.5 2.61032 4.57902 2.46967 4.71967C2.32902 4.86032 2.25 5.05109 2.25 5.25C2.25 5.44891 2.32902 5.63968 2.46967 5.78033C2.61032 5.92098 2.80109 6 3 6H3.75V14.25C3.75 14.8467 3.98705 15.419 4.40901 15.841C4.83097 16.2629 5.40326 16.5 6 16.5H12C12.5967 16.5 13.169 16.2629 13.591 15.841C14.0129 15.419 14.25 14.8467 14.25 14.25V6H15C15.1989 6 15.3897 5.92098 15.5303 5.78033C15.671 5.63968 15.75 5.44891 15.75 5.25C15.75 5.05109 15.671 4.86032 15.5303 4.71967C15.3897 4.57902 15.1989 4.5 15 4.5ZM7.5 3.75C7.5 3.55109 7.57902 3.36032 7.71967 3.21967C7.86032 3.07902 8.05109 3 8.25 3H9.75C9.94891 3 10.1397 3.07902 10.2803 3.21967C10.421 3.36032 10.5 3.55109 10.5 3.75V4.5H7.5V3.75ZM12.75 14.25C12.75 14.4489 12.671 14.6397 12.5303 14.7803C12.3897 14.921 12.1989 15 12 15H6C5.80109 15 5.61032 14.921 5.46967 14.7803C5.32902 14.6397 5.25 14.4489 5.25 14.25V6H12.75V14.25ZM10.5 13.5C10.6989 13.5 10.8897 13.421 11.0303 13.2803C11.171 13.1397 11.25 12.9489 11.25 12.75V8.25C11.25 8.05109 11.171 7.86032 11.0303 7.71967C10.8897 7.57902 10.6989 7.5 10.5 7.5C10.3011 7.5 10.1103 7.57902 9.96967 7.71967C9.82902 7.86032 9.75 8.05109 9.75 8.25V12.75C9.75 12.9489 9.82902 13.1397 9.96967 13.2803C10.1103 13.421 10.3011 13.5 10.5 13.5Z' fill='#C2C2C2'/>
                            </svg>
                            <p>Delete</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                <Modal
                  open={shareOpen}
                  onClose={() => setShareOpen(false)}
                >
                  <CaseShareModal
                    caseId={case_id}
                    date={formatted_dt}
                    setShareOpen={setShareOpen}
                    values={values}
                    submitForm={submitForm}
                    curUser={cur_user}
                    userOpts={userOpts}
                    setFieldValue={setFieldValue}
                  />
                </Modal>
                <Modal
                  open={deleteOpen}
                  onClose={() => setDeleteOpen(false)}
                >
                  <CaseDeleteModal handleDeleteCase={handleDeleteCase} setDeleteOpen={setDeleteOpen} id={case_id} />
                </Modal>
              </Grid>

              <Grid item xs={12} md={8} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <CaseName ticketName={values.ticketName} edit={cur_case.data.editing?.kc_id === cur_user.basics?.id || !cur_case.data.ticketName} />
                { cur_case.data.docType === 'bulkScore' ? (
                  <div className='overflow-hidden'>
                    <SearchedProps rowData={values.searchedProps}/>
                  </div>

                ) : (
                  <div>
                    <TabPanel value={tabValue} index={0} className=''>
                      <EntityForm entity={values.entity} edit={(cur_case.data.docType === 'report' && cur_case.data.editing?.kc_id === cur_user.basics?.id) || (!cur_case.data.ticketName)} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1} className=''>
                      <CommodityForm commodities={values.commodities} setFieldValue={setFieldValue} edit={(cur_case.data.docType === 'report' && cur_case.data.editing?.kc_id === cur_user.basics?.id) || (!cur_case.data.ticketName)}/>
                    </TabPanel>
                  </div>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                {(cur_case.data.docType === 'report' && cur_case.data.editing?.kc_id === cur_user.basics?.id) || (!cur_case.data.ticketName) || (values?.documents[0]) ? (
                  <PDFUpload
                    setFieldValue={setFieldValue}
                    documents={values.documents}
                    existingCase={existingCase}
                  />
                ) : (
                  <div className='vault-pdf-container'>
                    <div className='vault-doc-error'>
                      <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'>
                        <path d='M21.3333 26.668H10.6667C9.6058 26.668 8.58839 26.2465 7.83824 25.4964C7.08809 24.7463 6.66667 23.7288 6.66667 22.668V9.33464C6.66667 8.98101 6.52619 8.64188 6.27614 8.39183C6.02609 8.14178 5.68696 8.0013 5.33333 8.0013C4.97971 8.0013 4.64057 8.14178 4.39052 8.39183C4.14048 8.64188 4 8.98101 4 9.33464V22.668C4 24.4361 4.70238 26.1318 5.95262 27.382C7.20286 28.6323 8.89856 29.3346 10.6667 29.3346H21.3333C21.687 29.3346 22.0261 29.1942 22.2761 28.9441C22.5262 28.6941 22.6667 28.3549 22.6667 28.0013C22.6667 27.6477 22.5262 27.3085 22.2761 27.0585C22.0261 26.8084 21.687 26.668 21.3333 26.668ZM13.3333 17.3346C13.3333 17.6883 13.4738 18.0274 13.7239 18.2774C13.9739 18.5275 14.313 18.668 14.6667 18.668H21.3333C21.687 18.668 22.0261 18.5275 22.2761 18.2774C22.5262 18.0274 22.6667 17.6883 22.6667 17.3346C22.6667 16.981 22.5262 16.6419 22.2761 16.3918C22.0261 16.1418 21.687 16.0013 21.3333 16.0013H14.6667C14.313 16.0013 13.9739 16.1418 13.7239 16.3918C13.4738 16.6419 13.3333 16.981 13.3333 17.3346ZM28 11.9213C27.9861 11.7988 27.9593 11.6781 27.92 11.5613V11.4413C27.8559 11.3042 27.7704 11.1782 27.6667 11.068L19.6667 3.06797C19.5564 2.96426 19.4304 2.87874 19.2933 2.81464C19.2535 2.80898 19.2131 2.80898 19.1733 2.81464C19.0379 2.73696 18.8883 2.68709 18.7333 2.66797H13.3333C12.2725 2.66797 11.2551 3.0894 10.5049 3.83954C9.75476 4.58969 9.33333 5.6071 9.33333 6.66797V20.0013C9.33333 21.0622 9.75476 22.0796 10.5049 22.8297C11.2551 23.5799 12.2725 24.0013 13.3333 24.0013H24C25.0609 24.0013 26.0783 23.5799 26.8284 22.8297C27.5786 22.0796 28 21.0622 28 20.0013V12.0013C28 12.0013 28 12.0013 28 11.9213ZM20 7.21464L23.4533 10.668H21.3333C20.9797 10.668 20.6406 10.5275 20.3905 10.2774C20.1405 10.0274 20 9.68826 20 9.33464V7.21464ZM25.3333 20.0013C25.3333 20.3549 25.1929 20.6941 24.9428 20.9441C24.6928 21.1942 24.3536 21.3346 24 21.3346H13.3333C12.9797 21.3346 12.6406 21.1942 12.3905 20.9441C12.1405 20.6941 12 20.3549 12 20.0013V6.66797C12 6.31435 12.1405 5.97521 12.3905 5.72516C12.6406 5.47511 12.9797 5.33464 13.3333 5.33464H17.3333V9.33464C17.3369 9.78949 17.4181 10.2404 17.5733 10.668H14.6667C14.313 10.668 13.9739 10.8084 13.7239 11.0585C13.4738 11.3085 13.3333 11.6477 13.3333 12.0013C13.3333 12.3549 13.4738 12.6941 13.7239 12.9441C13.9739 13.1942 14.313 13.3346 14.6667 13.3346H25.3333V20.0013Z' fill='white'/>
                      </svg>
                      <h2 >
                        No PDF on this ticket
                      </h2>
                    </div>
                  </div>
                )
                }
              </Grid>
              {cur_case.data.docType === 'bulkScore' ? (
                <Grid item xs={12}>
                  <ResultsTable
                    hits={values.results}
                  />
                </Grid>
              ) : (
                <></>
              )

              }
              <Grid item xs={12}>
                <Comments
                  comments={values.comments}
                  setFieldValue={setFieldValue}
                  edit={cur_case.data.editing?.kc_id === cur_user.basics?.id || !cur_case.data.ticketName}
                  cur_user={cur_user}
                  formatted_dt={formatted_dt}
                />
              </Grid>
              <Grid item xs={12}>
                <div className='stepper-btns-container'>
                  {cur_case.data.editing?.kc_id === cur_user.basics?.id ?
                    (
                      <div className='notify-switch'>
                        <p className='notify-text'>Notify Team Members of Update?</p>
                        <CustomLabel
                          control={<CustomSwitch checked={notify} onChange={() => setNotify(!notify)} name='notify' />}
                          label={notify ? 'Yes' : 'No'}
                        />
                      </div>
                    ) : (
                      <div></div>
                    )
                  }<div className='case-btns'>
                    <BackButton onClick={resetForm} disabled={cur_case.data.editing?.kc_id !== cur_user.basics?.id && cur_case.data.ticketName}>
                      {existingCase ? 'Undo' : 'Clear'}
                      <div className='finish-page-svg-spacer'>
                        <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                          <path d='M11.25 5.13281C11.25 5.13281 12.1064 4.71094 9 4.71094C7.88748 4.71094 6.79995 5.04084 5.87492 5.65892C4.94989 6.277 4.22892 7.15551 3.80318 8.18334C3.37744 9.21118 3.26604 10.3422 3.48309 11.4333C3.70013 12.5245 4.23586 13.5267 5.02253 14.3134C5.8092 15.1001 6.81148 15.6358 7.90262 15.8529C8.99376 16.0699 10.1248 15.9585 11.1526 15.5328C12.1804 15.107 13.0589 14.386 13.677 13.461C14.2951 12.536 14.625 11.4485 14.625 10.3359' stroke='#F5FAFF' strokeWidth='1.5' strokeMiterlimit='10' strokeLinecap='round'/>
                          <path d='M9 2.03906L11.8125 4.85156L9 7.66406' stroke='#F5FAFF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                        </svg>
                      </div>
                    </BackButton>
                    <NextButton onClick={submitForm} disabled={cur_case.data.editing?.kc_id !== cur_user.basics?.id && cur_case.data.ticketName}>
                      {existingCase ? 'Update' : 'Submit'}
                    </NextButton>
                  </div>

                </div>
              </Grid>
            </Grid>
          </div>
        </Form>
      }
    </Formik>
  );
};

export default Case;
